import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Reveal } from '@/components/Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sparkles, Sun, CloudSun, Sofa, Wand2, Trash2,
  ArrowRight, Camera, Clock, ShieldCheck,
} from 'lucide-react';

const enhancements = [
  { icon: Trash2, title: 'Declutter', desc: 'Remove personal items, cables, and clutter while keeping the room layout intact.' },
  { icon: Sun, title: 'Golden hour lighting', desc: 'Reshoot every shot with warm, late-afternoon natural light — exteriors and interiors.' },
  { icon: CloudSun, title: 'Sky replacement', desc: 'Swap gray, blown-out skies for clean blue or sunset — only when the photo needs it.' },
  { icon: Sofa, title: 'Virtual staging', desc: 'Furnish empty rooms in modern, Scandinavian, or coastal styles — per photo.' },
  { icon: Wand2, title: 'Custom prompt edits', desc: 'Tell the AI exactly what to change — "open the curtains", "remove the car", and more.' },
  { icon: Sparkles, title: 'Smart enhancement', desc: 'Color balance, lens correction, glare removal, and sharpening applied automatically.' },
];

const stats = [
  { value: '~10', unit: 'min', label: 'From upload to a full set of enhanced photos' },
  { value: '6', unit: 'AI rules', label: 'Applied intelligently, per photo' },
  { value: '$15', unit: 'one-time', label: 'No subscription required' },
];

const faqs = [
  {
    q: 'Is this a real AI transformation or a stock before/after?',
    a: 'It is real. The video shows actual ListBright outputs — the same enhancements your buyers will see on every listing you generate.',
  },
  {
    q: 'Which AI model powers the photo enhancements?',
    a: 'ListBright uses Google\'s latest image models (Nano Banana 2 and 1) tuned with property-specific rules — declutter, golden hour, sky replace, and virtual staging.',
  },
  {
    q: 'Will the enhanced photos look fake?',
    a: 'No. ListBright keeps the original room layout, windows, and architecture. Only lighting, clutter, sky, and (optionally) furniture change.',
  },
  {
    q: 'Can I pick which enhancements run per photo?',
    a: 'Yes. Each photo has its own controls — apply virtual staging to one room, golden hour to the exterior, declutter to the kitchen, and so on.',
  },
  {
    q: 'How much does photo enhancement cost?',
    a: 'It\'s included in every Single Property Kit ($15) and every subscription plan. 10 photos are included; upgrade to 20 for $5.',
  },
];

export default function AiPhotoTransformation() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'AI Real Estate Photo Transformation — Before & After',
      description:
        'Watch raw listing photos transform live with AI: declutter, golden hour lighting, sky replacement, and virtual staging — applied automatically.',
      thumbnailUrl: 'https://listbright.ai/logo.png',
      uploadDate: '2026-05-01',
      contentUrl: 'https://listbright.ai/enhancement-morph-v3.mp4',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="AI Real Estate Photo Transformation Demo"
        description="See AI declutter, relight, sky-replace, and virtually stage real estate photos in real time. Real outputs from ListBright — no stock before/afters."
        canonical="https://listbright.ai/ai-photo-transformation"
        ogType="video.other"
        jsonLd={jsonLd}
        breadcrumbs={[{ label: 'AI Photo Transformation', path: '/ai-photo-transformation' }]}
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-border/40 py-20 lg:py-28">
        {/* Layered background */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div
          className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute top-[-6rem] right-1/4 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-8rem] left-1/4 w-80 h-80 bg-accent/40 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge
              variant="secondary"
              className="mb-5 bg-primary/10 text-primary border-0 px-4 py-1.5 animate-fade-in"
            >
              <span className="relative flex h-2 w-2 mr-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              See the AI transformation live
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-5 tracking-tight leading-[1.05] animate-fade-up">
              AI Real Estate Photos,{' '}
              <span className="text-gradient">Transformed Live</span>
            </h1>
            <p className="text-muted-foreground text-lg lg:text-xl leading-relaxed animate-fade-up" style={{ animationDelay: '80ms' }}>
              Real AI enhancements, generated in front of you — the same outputs your buyers see on
              every listing. Declutter, golden hour, sky replacement, and virtual staging, applied
              automatically.
            </p>
          </div>

          <Reveal className="relative mx-auto max-w-4xl">
            <div className="absolute -inset-6 bg-gradient-to-tr from-primary/25 via-primary/10 to-transparent blur-3xl rounded-[2.5rem] opacity-70 pointer-events-none" />
            <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-elevated border border-border/50 bg-card">
              {/* Live chip */}
              <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur text-white text-[11px] font-medium">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                </span>
                Live AI output
              </div>
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="block w-full aspect-video"
                aria-label="AI real estate photo transformation video"
              >
                <source src="/enhancement-morph-v3.mp4" type="video/mp4" />
              </video>
            </div>
          </Reveal>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate('/auth')} className="group shadow-lg">
              Transform your photos free
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <p className="text-sm text-muted-foreground">From $15 · One-time payment · Ready in ~10 minutes</p>
          </div>
        </div>
      </section>

      {/* ── Stats strip ── */}
      <section className="border-b border-border/40 bg-card/40">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border/50">
            {stats.map((s, i) => (
              <Reveal key={s.unit} delay={i * 80} className="py-8 px-6 text-center sm:text-left">
                <div className="flex items-baseline gap-1.5 justify-center sm:justify-start">
                  <span className="text-3xl sm:text-4xl font-display font-bold text-primary">{s.value}</span>
                  <span className="text-sm font-semibold text-foreground">{s.unit}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Enhancements ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-6xl mx-auto px-4">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
              <Sparkles className="w-4 h-4 text-primary" /> What the AI does
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">
              Six AI enhancements, applied to every photo
            </h2>
            <p className="text-muted-foreground text-lg">
              You don't pick a preset — ListBright reads each photo and applies the right combination automatically.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {enhancements.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={(i % 3) * 80}>
                <div className="card-premium h-full rounded-2xl border border-border/60 bg-card p-6 group">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 transition-colors group-hover:bg-primary/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-bold text-lg mb-1.5">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trust band ── */}
      <section className="py-16 lg:py-20 border-b border-border/40 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Camera, title: 'Layout stays real', desc: 'Windows, walls, and architecture are preserved — only lighting, clutter, and styling change.' },
              { icon: ShieldCheck, title: 'Original always kept', desc: 'Every enhanced photo keeps its source, so you can disclose staged shots with confidence.' },
              { icon: Clock, title: 'Whole listing in minutes', desc: 'Not one photo at a time — an entire gallery enhanced in a single pass.' },
            ].map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 80} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 lg:py-28 border-b border-border/40">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-3">Frequently asked</h2>
            <p className="text-muted-foreground">Everything you need to know before your first transformation.</p>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map(({ q, a }, i) => (
                <AccordionItem key={q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">{q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 lg:py-24">
        <div className="container max-w-3xl mx-auto px-4">
          <Reveal className="text-center space-y-6 p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
            <h2 className="text-3xl sm:text-4xl font-display font-bold">
              Transform your listing photos today
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              Upload your shots and watch the same AI enhancements run on your own property — free to start.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Button size="lg" onClick={() => navigate('/auth')} className="group text-base px-8 shadow-lg">
                Transform your photos free
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/pricing')} className="text-base">
                See pricing
              </Button>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              No credit card required · First listing free · Ready in ~10 minutes
            </p>
            <div className="pt-2">
              <Link to="/how-it-works" className="text-primary hover:underline text-sm">
                Next: Watch the full ListBright walkthrough →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
